import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { generalLedgerService, GLJournalEntryListDTO } from '../../../../services/accountingService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { CirclePlus, RefreshCw, Eye, ScrollText } from 'lucide-react';

const STATUS_BADGE: Record<string, string> = {
  Draft: 'ims-badge ims-badge-draft',
  Posted: 'ims-badge ims-badge-completed',
  Voided: 'ims-badge ims-badge-closed',
};

const JournalEntriesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();

  const [entries, setEntries] = useState<GLJournalEntryListDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';
  const fmtDate = (d: string) => new Date(d).toLocaleDateString(langCode);

  const load = async (spinner: (v: boolean) => void) => {
    spinner(true);
    try {
      const res = await generalLedgerService.getJournalEntries({ pageNumber: 1, pageSize: 100 });
      const data = res.data?.data;
      if (Array.isArray(data)) setEntries(data);
    } finally {
      spinner(false);
    }
  };

  useEffect(() => { load(setLoading); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <div style={{ padding: '0 20px 40px', maxWidth: 1000, margin: '0 auto' }}>
        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('accounting.gl.journalEntries')}</h1>
          <div className="ims-list-actions">
            <button className="btn-new" onClick={() => navigate('/accounting/journal-entries/create')}>
              <CirclePlus size={18} strokeWidth={1.75} />
              {t('accounting.gl.newEntry')}
            </button>
            <button className="btn-refresh" onClick={() => load(setRefreshing)} disabled={refreshing}>
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {t('common.refresh')}
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
          </div>
        ) : entries.length === 0 ? (
          <div className="ims-card" style={{ textAlign: 'center', padding: 40, color: 'var(--ims-text-muted)' }}>
            <ScrollText size={40} strokeWidth={1} style={{ marginBottom: 8 }} />
            <div>{t('accounting.gl.noEntries')}</div>
          </div>
        ) : (
          <div className="ims-card">
            <div className="ims-table-scroll">
              <table className="ims-table">
                <thead>
                  <tr>
                    <th className="ims-th" style={{ width: 150 }}>{t('accounting.ledger.entryNumber')}</th>
                    <th className="ims-th" style={{ width: 110 }}>{t('accounting.ledger.date')}</th>
                    <th className="ims-th">{t('common.description')}</th>
                    <th className="ims-th" style={{ width: 100 }}>{t('accounting.gl.source')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 120 }}>{t('accounting.gl.amount')}</th>
                    <th className="ims-th" style={{ width: 100 }}>{t('common.status')}</th>
                    <th className="ims-th" style={{ width: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="ims-tr">
                      <td className="ims-td"><code>{e.entryNumber}</code></td>
                      <td className="ims-td">{fmtDate(e.entryDate)}</td>
                      <td className="ims-td">{e.description || '—'}</td>
                      <td className="ims-td">{t(`accounting.sourceModule.${e.sourceModule}`)}</td>
                      <td className="ims-td ims-td--line-total">{fmt(e.totalDebit)}</td>
                      <td className="ims-td">
                        <span className={STATUS_BADGE[e.status] ?? 'ims-badge ims-badge-draft'}>
                          {t(`accounting.journalStatus.${e.status}`)}
                        </span>
                      </td>
                      <td className="ims-td">
                        <button className="grid-action-btn-view" title={t('common.view')}
                          onClick={() => navigate(`/accounting/journal-entries/${e.id}`)}>
                          <Eye size={16} strokeWidth={1.75} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default JournalEntriesListPage;
